import React, { useState } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
    IconButton, Snackbar, Alert
} from "@mui/material";
import { Add, Delete } from "@mui/icons-material";

import {
    useAddProductPropertyMutation,
    useDeleteProductPropertyMutation,
    useGetProductDetailQuery,
} from "../../features/admin/productApi.ts";

interface Props {
    productId: number | null;
}

const PropertyManager: React.FC<Props> = ({ productId }) => {
    const { data: productDetail, refetch } = useGetProductDetailQuery(
        { product_id: productId as number },
        { skip: !productId }
    );

    const [addProperty] = useAddProductPropertyMutation();
    const [deleteProperty] = useDeleteProductPropertyMutation();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ property_name: "", property_value: "" });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Parse properties JSON from productDetail.result.properties
    const properties = Array.isArray(productDetail?.result?.properties)
        ? productDetail.result.properties
        : [];

    const handleOpen = () => {
        setForm({ property_name: "", property_value: "" });
        setDialogOpen(true);
    };

    const handleClose = () => setDialogOpen(false);

    const handleChange = (field: string, value: string) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!productId || !form.property_name || !form.property_value) return;
        try {
            const result = await addProperty({ product_id: productId, ...form }).unwrap();
            setSnackbar({ open: true, message: result.message, severity: "success" });
            setDialogOpen(false);
            refetch();
        } catch (err: any) {
            setSnackbar({ open: true, message: err?.data?.message || "Error adding property", severity: "error" });
        }
    };

    const handleDelete = async (name: string) => {
        if (!productId) return;
        if (!window.confirm(`Delete property "${name}"?`)) return;
        try {
            const result = await deleteProperty({ product_id: productId, property_name: name }).unwrap();
            setSnackbar({ open: true, message: result.message, severity: "success" });
            refetch();
        } catch (err: any) {
            setSnackbar({ open: true, message: err?.data?.message || "Delete failed", severity: "error" });
        }
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Button variant="outlined" startIcon={<Add />} onClick={handleOpen}>
                Add Properties
            </Button>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>S.No</TableCell>
                            <TableCell>Properties Name</TableCell>
                            <TableCell>Properties Value</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {properties.map((prop: any, idx: number) => {
                            const [name, value] = Object.entries(prop)[0] as [string, string]; 
                            return (
                                <TableRow key={idx}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>{name}</TableCell>
                                    <TableCell>{String(value)}</TableCell> 
                                    <TableCell align="center">
                                        <IconButton color="error" onClick={() => handleDelete(name)}>
                                            <Delete />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Add Properties</DialogTitle>
                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField
                        label="Properties Name"
                        value={form.property_name}
                        onChange={(e) => handleChange("property_name", e.target.value)}
                    />
                    <TextField
                        label="Properties Value"
                        value={form.property_value}
                        onChange={(e) => handleChange("property_value", e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert severity={snackbar.severity as any}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default PropertyManager;